import React, { ChangeEvent, FC, KeyboardEventHandler, ReactNode, useCallback } from 'react';
import styled from 'styled-components';
import { CommonComponentProps, CommonFieldProps } from '@clds/common-definitions';
import { typography } from '@clds/typography';
import { media } from '@clds/style-utils';
import { theme } from './FieldBaseTheme';

const Root = styled.div`
  background-color: ${theme.background.color};
  border: 1px solid ${theme.border.color.normal};
  border-radius: ${theme.border.radius};

  display: flex;
  align-items: center;

  &:focus-within {
    border: 1px solid ${theme.border.color.focus};
  }
`;

export const Input = styled.input`
  ${typography.xs.regular};
  background: none;
  border: none;
  height: 100%;
  flex: 1;
  align-self: stretch;
  outline: none;
  padding: ${theme.text.padding.vertical} ${theme.text.padding.horizontal};
  min-width: 150px;

  @media ${media.phone({ maxOnly: true })} {
    min-width: 100px;
  }
`;

export interface FieldBaseProps
  extends CommonComponentProps<'div'>,
    Omit<CommonFieldProps<string, ChangeEvent<HTMLInputElement>>, 'validationState' | 'isDisabled' | 'isReadonly'> {
  /**
   * Content to be added before actual text input component
   */
  beforeDecorator?: ReactNode;
  /**
   * Content to be added after actual text input component
   */
  afterDecorator?: ReactNode;
  /**
   * Input placeholder
   */
  placeholder?: string;
  /**
   * Called when user hits enter key when focused inside field
   */
  onEnter?: () => void;
  /**
   * Called when user hits backspace key when focused inside field
   */
  onBackspace?: () => void;
}

/**
 * Base primitive to display search component according to Cookbook design
 */
export const FieldBase: FC<React.PropsWithChildren<FieldBaseProps>> = ({
  rootProps,
  beforeDecorator,
  afterDecorator,
  onEnter,
  onBackspace,
  onChange,
  ...restProps
}) => {
  const keyHandler: KeyboardEventHandler = useCallback(
    (event) => {
      const handlers: Record<string, typeof onEnter> = { Enter: onEnter, Backspace: onBackspace };
      handlers[event.key]?.();
    },
    [onBackspace, onEnter]
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { children, ...inputProps } = restProps;

  return (
    <Root {...rootProps}>
      {beforeDecorator}
      <Input {...inputProps} onChange={(event) => onChange?.(event.currentTarget.value, event)} onKeyDown={keyHandler} />
      {afterDecorator}
    </Root>
  );
};
